import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { rbacService, roleV2Service, schoolAdminRoleV2Service, type RbacRolePayload } from '@/api/services/roleV2Service'
import { useAuthStore } from '@/store/useAuthStore'
import { authService } from '@/api/services/authService'
import { schoolRoleService } from '@/api/services/schoolRoleService'

// ── /v2/admin/rbac ───────────────────────────────────────────────────────────

/** Every permission key the admin can grant (from who-am-i) — the role editor's checkbox list. */
export function usePermissionCatalog(enabled = true) {
  return useQuery({
    queryKey: ['rbac', 'catalog'],
    queryFn: authService.permissionCatalog,
    enabled,
    staleTime: 300_000,
  })
}

/** The logged-in admin's own roles + effective permissions. */
export function useRbacMe() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated)
  return useQuery({
    queryKey: ['rbac', 'me'],
    queryFn: rbacService.me,
    enabled: isAuthenticated,
    staleTime: 300_000,
  })
}

export function useRbacRoles() {
  return useQuery({ queryKey: ['rbac', 'roles'], queryFn: rbacService.listRoles, staleTime: 300_000 })
}

export function useRbacRole(id?: number | null) {
  return useQuery({
    queryKey: ['rbac', 'roles', id],
    queryFn: () => rbacService.getRole(id as number),
    enabled: id != null,
  })
}

export function useCreateRbacRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (d: RbacRolePayload) => rbacService.createRole(d),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rbac', 'roles'] }),
  })
}

export function useUpdateRbacRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...d }: { id: number } & Partial<RbacRolePayload>) => rbacService.updateRole(id, d),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rbac'] }),
  })
}

export function useDeleteRbacRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => rbacService.deleteRole(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rbac', 'roles'] }),
  })
}

export function useSetRbacRolePermissions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, permission_keys, permission_ids }: { id: number; permission_keys: string[]; permission_ids?: number[] }) =>
      rbacService.setRolePermissions(id, permission_keys, permission_ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rbac'] }),
  })
}

export function useRbacUserRoles(userId?: number | null) {
  return useQuery({
    queryKey: ['rbac', 'user-roles', userId],
    queryFn: () => rbacService.getUserRoles(userId as number),
    enabled: userId != null,
  })
}

export function useSetRbacUserRoles() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role_ids }: { userId: number; role_ids: number[] }) =>
      rbacService.setUserRoles(userId, role_ids),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['rbac', 'user-roles', v.userId] })
      qc.invalidateQueries({ queryKey: ['rbac', 'me'] })
    },
  })
}

// ── /v2/admin/role (platform-admin mapping) ──────────────────────────────────

export function useRolesV2(p?: { page?: number; limit?: number }) {
  return useQuery({ queryKey: ['roles-v2', p], queryFn: () => roleV2Service.list(p), staleTime: 300_000 })
}

export function useSaveRoleV2() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (d: Record<string, unknown>) => roleV2Service.createUpdate(d),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles-v2'] }),
  })
}

export function useRolePermissionMappingV2(p?: { page?: number; limit?: number }) {
  return useQuery({ queryKey: ['roles-v2', 'permission-mapping', p], queryFn: () => roleV2Service.permissionList(p) })
}

export function useMapRolePermissionsV2() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (d: Record<string, unknown>) => roleV2Service.mapPermissions(d),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles-v2'] }),
  })
}

export function useRoleUserMappingV2(p?: { page?: number; limit?: number }) {
  return useQuery({ queryKey: ['roles-v2', 'user-mapping', p], queryFn: () => roleV2Service.userMappingList(p) })
}

export function useMapRoleUsersV2() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (d: { role_id: number[]; user_id: number[] }) => roleV2Service.mapUsers(d),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles-v2'] }),
  })
}

// ── School-admin access control ──────────────────────────────────────────────
// A platform admin manages school-admin roles through /v2/admin/access-control-for-school-admin;
// a school admin manages their own school's through the `…ForSchool` GraphQL operations.
// Both services expose the same methods, so the hooks just pick one.

const useSchoolRoleApi = () => {
  const isSchoolAdmin = useAuthStore(s => s.user?.user_type === 'school_admin')
  return { isSchoolAdmin, api: isSchoolAdmin ? schoolRoleService : schoolAdminRoleV2Service }
}
const KEY = 'school-admin-roles-v2'

export function useSchoolAdminRolesV2() {
  const { isSchoolAdmin, api } = useSchoolRoleApi()
  return useQuery({ queryKey: [KEY, isSchoolAdmin, 'roles'], queryFn: () => api.list(), staleTime: 300_000 })
}

export function useCreateSchoolAdminRoleV2() {
  const qc = useQueryClient()
  const { api } = useSchoolRoleApi()
  return useMutation({
    mutationFn: (d: { name: string; description?: string }) => api.create(d),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  })
}

export function useSchoolAdminPermissionsV2(enabled = true) {
  const { isSchoolAdmin, api } = useSchoolRoleApi()
  return useQuery({ queryKey: [KEY, isSchoolAdmin, 'permissions'], queryFn: () => api.listAllPermissions(), enabled, staleTime: 300_000 })
}

/** What a school-admin role is currently allowed to do (one row per permission it has). */
export function useSchoolAdminRolePermissionsV2(roleId?: number | null) {
  const { isSchoolAdmin, api } = useSchoolRoleApi()
  return useQuery({
    queryKey: [KEY, isSchoolAdmin, 'permission-mapping', roleId],
    queryFn: () => api.permissionMapList(roleId as number),
    enabled: roleId != null,
  })
}

export function useMapSchoolAdminPermissionsV2() {
  const qc = useQueryClient()
  const { api } = useSchoolRoleApi()
  return useMutation({
    mutationFn: (d: Parameters<typeof schoolRoleService.mapPermissions>[0]) => api.mapPermissions(d),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  })
}

export function useSchoolAdminUserRoleMappingV2(p?: { user_id?: number; role_id?: number }, enabled = true) {
  const { isSchoolAdmin, api } = useSchoolRoleApi()
  return useQuery({ queryKey: [KEY, isSchoolAdmin, 'user-mapping', p], queryFn: () => api.userMappingList(p), enabled })
}

export function useMapSchoolAdminRoleUsersV2() {
  const qc = useQueryClient()
  const { api } = useSchoolRoleApi()
  return useMutation({
    mutationFn: (d: { role_id: number[]; user_id: number[] }) => api.mapUsers(d),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  })
}
